import { isValidElement, type ReactNode, useMemo, useState } from "react";
import { StyleSheet, type TextStyle, View, type ViewStyle } from "react-native";
import type { StyleProp } from "react-native/Libraries/StyleSheet/StyleSheet";
import type { RadioButtonGroupProps } from "react-native-paper";
import RadioGroup, { type RadioButtonProps } from "react-native-radio-buttons-group";
import { FieldLabel } from "~/components/form/FieldLabel";
import { BaseText, BodyText } from "~/components/layout/text";
import { Flex, Gap, Items } from "~/constants/layout";
import { Space } from "~/constants/sizing";
import { NoName, useErrorBag } from "~/providers/ErrorBagProvider";
import { useTheme } from "~/providers/ThemeProvider";
import { filled } from "~/util";
import { itemWidth, setWidthOnLayout } from "~/util/layout";

export interface RadioOption<T extends string = string> {
    value: T;
    label: ReactNode;
    subtitle?: ReactNode;
    disabled?: boolean;
}

export type RadioGroupProps<T extends string = string> = {
    title?: ReactNode;
    name?: string;
    error?: string | null;
    options: T[] | RadioOption<T>[];
    optional?: boolean;
    disabled?: boolean;
    labelStyle?: StyleProp<TextStyle>;
    itemStyle?: StyleProp<ViewStyle>;
    value: T | null;
    columns?: number;
    onValueChange: (value: T) => void;
} & Omit<RadioButtonGroupProps, "value" | "children" | "onValueChange">;

const COLUMN_GAP = Space.sm;

export function RadioSelector<T extends string = string>({
    title,
    error,
    options,
    value,
    disabled,
    optional,
    name = NoName,
    columns = 1,
    labelStyle,
    itemStyle,
    onValueChange,
}: RadioGroupProps<T>) {
    const { colours, inputs } = useTheme();
    const errorBag = useErrorBag();
    const [width, setWidth] = useState(100);
    const onLayout = setWidthOnLayout(setWidth);
    const item_width = useMemo(() => itemWidth(Math.floor(width), columns, COLUMN_GAP), [width, columns]);

    const field_error = error ?? errorBag.first(name);

    const handleChange = (value: string) => {
        errorBag.forget(name);
        onValueChange(value as T);
    };

    const selectedId = filled(value) ? value : undefined;
    const buttons: RadioButtonProps[] = useMemo(
        () =>
            options.map((option) => {
                const { value, label, ...opts } =
                    typeof option === "object" ? option : { value: option, label: `${option}` };

                const id = `${value}`;
                const selected = selectedId === value;

                const color = selected ? colours.check.selector : colours.input.border;

                return {
                    id,
                    value,
                    disabled: disabled || opts?.disabled,
                    label: opts?.subtitle ? (
                        <View style={styles.itemText}>
                            <BaseText size="md">{label}</BaseText>
                            <BodyText size="md" colour="secondary">
                                {opts.subtitle}
                            </BodyText>
                        </View>
                    ) : isValidElement(label) ? (
                        label
                    ) : (
                        <BodyText size="md">{label}</BodyText>
                    ),
                    borderColor: color,
                    color,
                    size: 20,
                    containerStyle: [
                        inputs.input,
                        styles.item,
                        Flex.start,
                        {
                            width: item_width,
                            backgroundColor: selected ? colours.check.background : colours.background.primary,
                            borderColor: selected ? colours.check.selector : colours.input.border,
                        },
                        itemStyle,
                    ],
                } as RadioButtonProps;
            }),
        [options, colours, inputs, selectedId, disabled, item_width, itemStyle],
    );

    return (
        <View style={[Gap.sm, Items.stretch]} onLayout={onLayout}>
            <FieldLabel label={title} labelStyle={labelStyle} optional={optional} error={field_error} />
            <RadioGroup
                radioButtons={buttons}
                onPress={handleChange}
                selectedId={selectedId}
                containerStyle={[styles.group, { flex: columns }]}
            />
        </View>
    );
}

const styles = StyleSheet.create({
    group: {
        flexDirection: "row",
        flexWrap: "wrap",
        alignItems: "stretch",
        gap: COLUMN_GAP,
    },
    item: {
        height: "auto",
        gap: Space.sm,
        alignItems: "center",
        marginHorizontal: 0,
        marginVertical: 0,
    },
    itemText: {
        justifyContent: "flex-start",
        alignItems: "stretch",
        marginRight: Space.lg,
        flexShrink: 1,
    },
});
